
import {
  readAssertedCompanyId,
  readAssertedUserProfile,
} from '@/store/main/getters';
import { commitAddNotification } from '@/store/main/mutations';
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
  nextTick,
} from 'vue';
import { NavigationGuardNext, Route } from 'vue-router';
import { AxiosError } from 'axios';
import { ApiErrorResponse } from '@/interfaces/api/general/errorResponses';
import { DynamicList } from '@/interfaces/api/general/dynamicList';
import { FormItem } from '@/interfaces/lib/model';
import { Sds, SdsDynamicListResult } from '@/interfaces/model/general/sds';
import { Department } from '@/interfaces/model/general/department';
import { MAX_LIMIT } from '@/lib/constant';
import { getPdfDisplayOptions, getStatusList } from '@/lib/formItem';
import { removeLastPdfExtension } from '@/lib/formatter';
import { casNumberPattern } from '@/lib/regex';
import router from '@/router';
import { store } from '@/store/index';
import {
  dispatchCancelCreateSimpleExportRequest,
  dispatchDeleteDynamicListResult,
  dispatchGetDynamicLists,
  dispatchGetExportCreateSimple,
  dispatchGetExportCreateSimpleV3,
  dispatchGetExportCreateSimpleV3_2,
  dispatchPostDynamicListResult,
  dispatchPutDynamicListResult,
  dispatchUpdateCompanySdses,
  dispatchGetUserSearchCriteria,
} from '@/store/main/actionsMain';
import SdsBottomSheet from '@/views/dashboard/bottomSheet/SdsBottomSheet.vue';
import CsvValidationDialog from '@/views/dashboard/bottomSheet/CsvValidationDialog.vue';
import RiskReassessmentCsvErrorDialog from '@/views/dashboard/bottomSheet/RiskReassessmentCsvErrorDialog.vue';
import CsvBulkUpdateProcessingDialog from '@/views/dashboard/bottomSheet/CsvBulkUpdateProcessingDialog.vue';
import PdfBulkDownloadProcessingDialog from '@/views/dashboard/bottomSheet/PdfBulkDownloadProcessingDialog.vue';
import CompanySdsTable from '@/views/dashboard/CompanySdsTable.vue';
import DownloadProgress from '@/views/dashboard/DownloadProgress.vue';
import SdsSearchForm from '@/views/dashboard/SdsSearchForm.vue';
import SdsFilterForm from '@/views/pages/sdsIndex/SdsFilterForm.vue';
import SdsCurrentFilter from '@/views/pages/sdsIndex/SdsCurrentFilter.vue';
import SdsTableDrawer from '@/views/dashboard/sdsDrawer/SdsTableDrawer.vue';
import {
  useSdsComposable,
  SortState,
  ActualUseSdsComposable,
} from '@/composables/useSds';
import ExportSdses from '@/views/pages/sdsIndex/ExportSdses.vue';
import { Tag, TagResponse } from '@/interfaces/model/general/tag';
import {
  UpdateCompanySdsesRequestBody,
  UserSearchCriteria,
} from '@/interfaces/api/general/company';
import { useSelectedItemsComposable } from '@/composables/useSelectedItems';
import { useUserComposable } from '@/composables/useUser';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { usePermissionComposable } from '@/composables/usePermission';
import { useTagComposable } from '@/composables/useTag';
import { NewSds } from '@/lib/constructor/model/general/sds';
import { useDownloadExportSdses } from '@/composables/useDownloadExportSdses';
import { useSdsesExport } from '@/composables/useSdsesExport';
import {
  convertFilterParamsToRequest,
  SdsesFilterParamsKeys,
  SdsesFilterParamsValue,
  useSdsFilter,
} from '@/composables/useSdsFilter';
import { User } from '@/interfaces/vuex/user';
import { defineMessages } from '@/lib/i18n';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { readUserLocale } from '@/store/main/getters';
import { AssignedUser } from '@/interfaces/model/general/assignee_user';
import VueI18n from 'vue-i18n';
import {
  fixStr,
  csvItem,
  areSetsEqual,
  updateChangeDateType,
  filterDuplicateTextInArray,
  updateCSVFileDateFormatType,
} from '@/utils/csvValidation';
import { isValidDateFormat } from '@/utils/datetime';
import { departmentNamePattern } from '@/lib/regex';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import MyFilterSelector from '@/views/pages/sdsIndex/MyFilterSelector.vue';
import { updateSdsCount } from '@/signals/UpdateSdsCount';

const locale = computed(() => readUserLocale(store));

const $t = i18n.t.bind(i18n);
const pdfDisplayOptions = getPdfDisplayOptions();
const statusList = getStatusList();

// 備考の最大入力可能文字数
const SDS_NOTE_MAX_LENGTH = 10000;

const m = defineMessages({
  updateSuccess: '選択したSDSを更新しました。',
  totalCount: '全 {total}件',
  csvDateLimit: '10000以上のデータをアップロードできません。',
  csvDownloadFail: 'CSVのダウンロードに失敗しました。',
  encodeTypeInvalid: 'フィールド内の文字エンコードが無効です。',
  sdsIdIsNotNull: 'SDS IDは空にできません。',
  sdsIdIsNotExist: 'SDS IDに該当するSDSが存在しません。',
  sdsNameIsNotNull: 'SDS名は空にできません。',
  sdsFileIsNotPdf: 'ファイルがpdfではありません。',
  sdsDepartmentCharatorIssue:
    '部署名には日本語、アルファベット、数字などを使用してください（文頭には特殊文字は使用できません）。',
  sdsDepartmentNonExist: '存在しない部署名が含まれています。',
  sdsDepartmentNonEdit: '管理者以外は、他部署のSDSを編集することはできません。',
  sdsTagNameInvalid: '存在しないタグが含まれています。',
  sdsNoteTooLong: '備考は{max}文字以内で入力してください。',
  sdsRiskAssessDateType:
    'リスクアセスメント実施日は YYYY/MM/DD 形式である必要があります。',
  sdsSupplierConfirmationDateType:
    'サプライヤーへの確認日は YYYY/MM/DD 形式である必要があります。',
  sdsDepartmentDuplicated: '同一の部署名が複数入力されています。',
  sdsTagDuplicated: '同一のタグ名が複数入力されています。',
  sdsProductCodeDuplicated: '同一の製品コード名が複数入力されています。',
  sdsAssignedUserDuplicated: '同一の担当ユーザーが複数入力されています。',
  sdsAssignedUserUnregistered: '登録されていないユーザーです。',
  sdsDateNonChanged: 'csvデータには変更はありません。',
  sdsFetchProcess: 'SDS データを取得しています...',
  sdsValidationFinished: '検証完了!',
  riskReassessmentUploadSuccess:
    'リスク再評価を受け付けました。完了後にメールで通知します。',
  riskReassessmentUploadFail:
    'リスク再評価CSVのアップロードに失敗しました。しばらくしてから再度お試しください。',
});

interface ErrorMsg {
  rowIndex: number;
  message: string;
}

interface ValidationResult {
  errorCount: number;
  sdsID: ErrorMsg[];
  pdfName: ErrorMsg[];
  riskAssessDate: ErrorMsg[];
  supplierConfirmationDate: ErrorMsg[];
  tag: ErrorMsg[];
  department: ErrorMsg[];
  productCode: ErrorMsg[];
  assignedUser: ErrorMsg[];
  notes: ErrorMsg[];
}

export default defineComponent({
  components: {
    SdsBottomSheet,
    CsvValidationDialog,
    RiskReassessmentCsvErrorDialog,
    CsvBulkUpdateProcessingDialog,
    PdfBulkDownloadProcessingDialog,
    CompanySdsTable,
    SdsTableDrawer,
    SdsSearchForm,
    SdsFilterForm,
    SdsCurrentFilter,
    DownloadProgress,
    ExportSdses,
    MyFilterSelector,
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): void {
    if (this.isEditing) {
      const isAllowed: boolean = window.confirm(
        $t(sharedMessages?.confirmLeave)
      );
      if (isAllowed) {
        next();
      }
    } else {
      next();
    }
  },
  setup() {
    const showSdsDetailView = ref<boolean>(false);
    const pdfName = ref<string>('');
    const keywordList = ref<Array<string>>([]);
    const productFormItems = ref<Array<{ text: string; value: number }>>([]);
    const materialFormItems = ref<Array<FormItem>>([]);
    const casFormItems = ref<Array<FormItem>>([]);
    const page = ref<number>(1);
    const limit = ref<number>(50);
    const isEditing = ref<boolean>(false);
    const isLoading = ref<boolean>(false);
    const isDownloadable = ref<boolean>(false);
    const isUploadable = ref<boolean>(false);
    const canExportCreateSimpleCount = ref<number>(0);
    const canExportCreateSimpleCountV3 = ref<number>(0);
    const dialogShow = ref<boolean>(false);

    const isChange = ref<number>(0);
    const isSdsFilterOpen = ref(false);
    const dynamicLists = ref<Array<DynamicList>>([]);
    const assigneeUsers = ref<User[]>([]);
    const activeSds = ref<Sds>(NewSds);
    const currentIndex = ref(0);

    const isVisibleCSVValidationDialog = ref<boolean>(false);
    const isVisibleCsvBulkUpdateProcessingDialog = ref<boolean>(false);
    const isVisiblePdfBulkDownloadProcessingDialog = ref<boolean>(false);
    const totalChangedNumber = ref<number>(0);
    const uploadProcessNumber = ref<number>(0);
    const fetchSdsesProcessString = ref<string>('');
    const nameChangedNumber = ref<number>(0);
    const riskAssessDateChangedNumber = ref<number>(0);
    const supplierConfirmationDateChangedNumber = ref<number>(0);
    const departmentChangedNumber = ref<number>(0);
    const tagChangedNumber = ref<number>(0);
    const productCodeChangedNumber = ref<number>(0);
    const assignedUserChangedNumber = ref<number>(0);
    const notesChangedNumber = ref<number>(0);

    const isDisableSdsBulkCsvDialog = ref<boolean>(true);
    const isDisableSdsRiskReassessmentDialog = ref<boolean>(true);
    const isVisibleRiskReassessmentErrorDialog = ref<boolean>(false);
    const riskReassessmentErrorMessages = ref<string[]>([]);
    const changedCsvData = ref<Array<csvItem>>([]);
    const validationErrorMsgs = ref<ValidationResult>({
      errorCount: 0,
      sdsID: [],
      pdfName: [],
      riskAssessDate: [],
      supplierConfirmationDate: [],
      tag: [],
      department: [],
      productCode: [],
      assignedUser: [],
      notes: [],
    });

    const userSearchCriteriaList = ref<UserSearchCriteria[]>([]);
    const activeMyFilter = ref<UserSearchCriteria>();

    const {
      sds,
      sdses,
      total,
      pdfBlob,
      wholeSds,
      pdfDownloadCount,
      pdfMonthlyLimit,
      editHistories,
      fetchCompanySds,
      fetchCompanySdses,
      fetchSdsPdf,
      fetchCompanySdsesCount,
      fetchCompanySdsesCountWithCondition,
      updateCompanySds,
      deleteCompanySdses,
      downloadSelectedSdsesCSV,
      downloadSelectedRiskReassessmentCSV,
      uploadSelectedSdsesCSV,
      uploadSelectedRiskReassessmentCSV,
      fetchDownloadPdfCount,
      downloadSelectedSdsesPDFBulk,
      fetchSdsEditHistories,
    } = useSdsComposable();
    const { users, getCompanyUsers } = useUserComposable();
    const {
      getCompanyDepartments,
      companyDepartments,
      departmentTree,
      getCompanyDepartmentTree,
      visibleCompanyDepartmentsByOwnOrRole,
    } = useDepartmentComposable();
    const { tags, getCompanyTag } = useTagComposable();
    const [
      selectedSdses,
      isSelectedAllSdses,
      isSelectedIndeterminateSdses,
      isSelectedWholeSdses,
      toggleSelectedSds,
      toggleAllSelectedSdses,
      toggleWholeSelectedSdses,
      resetSelectedSdses,
    ] = useSelectedItemsComposable<Sds>(
      sdses.value,
      (sds1, sds2) => sds1.uuid === sds2.uuid
    );
    const {
      hasPermission,
      hasSdsEditPermission,
      PERMISSIONS: P,
    } = usePermissionComposable();
    const {
      currentFilterParams,
      updateFormFilterParam,
      applyFormFilterParams,
      formFilterParams,
      statusOptions,
      userOptions,
      departmentOptions,
      dynamicListOptions,
      tagOptions,
      hasFilterParams,
      applyFromMyFilter,
    } = useSdsFilter(users, tags, companyDepartments, dynamicLists);
    const { isFeatureActive, COMPANY_FEATURES, generateInactiveMessage } =
      useCompanyFeatureComposable();

    const { downloadExportSdses } = useDownloadExportSdses();
    const { exportOperations, fetchCompanyExportOperations } = useSdsesExport();

    const sortState = ref<SortState>({
      name: 'none',
      revised_at: 'none',
      created_at: 'none',
      assessed_at: 'none',
      supplier_confirmed_at: 'none',
    });

    const handleClickProductName = (name: string): void => {
      updateFormFilterParam('productName', name);

      applyFilterParams();
    };

    const handleClickManufacturerName = (name: string): void => {
      updateFormFilterParam('manufacturerName', name);

      applyFilterParams();
    };

    const handleClickProductCode = (code: string): void => {
      updateFormFilterParam('productCode', code);

      applyFilterParams();
    };

    const handleClickTag = (tag: TagResponse): void => {
      const newParams = Array.from(
        new Set([...formFilterParams.value.tagIds, tag.id])
      );
      updateFormFilterParam('tagIds', newParams);

      applyFilterParams();
    };

    const handleClickDepartment = (department: Department): void => {
      const newParams = Array.from(
        new Set([...formFilterParams.value.departmentUuids, department.uuid])
      );
      updateFormFilterParam('departmentUuids', newParams);

      applyFilterParams();
    };

    const handleClickStatus = (statusGroupId: string): void => {
      const newParams = Array.from(
        new Set([...formFilterParams.value.statusGroupId, statusGroupId])
      );
      updateFormFilterParam('statusGroupId', newParams);

      applyFilterParams();
    };

    const handleClickAssigneeUsers = (assigneeUserId: string): void => {
      const newParams = Array.from(
        new Set([...formFilterParams.value.assigneeUsers, assigneeUserId])
      );
      updateFormFilterParam('assigneeUsers', newParams);

      applyFilterParams();
    };

    const removeFilterParams = (
      key: SdsesFilterParamsKeys,
      value: SdsesFilterParamsValue
    ) => {
      updateFormFilterParam(key, value);

      applyFilterParams();
    };

    const handleExportSubmit = () => {
      selectedSdses.value = [];
    };

    const toggleSort = (column: string): void => {
      if (sortState.value[column] === 'none') {
        sortState.value[column] = 'asc';
      } else if (sortState.value[column] === 'asc') {
        sortState.value[column] = 'desc';
      } else {
        sortState.value[column] = 'none';
      }
      Object.keys(sortState.value).forEach((key) => {
        if (key !== column) {
          sortState.value[key] = 'none';
        }
      });
      applyFilterParams();
    };

    const cancelExportCreateSimple = (): void => {
      dispatchCancelCreateSimpleExportRequest(store);
      dialogShow.value = false;
    };

    watch(showSdsDetailView, (next) => {
      if (next === false) {
        activeSds.value = NewSds;
      }
    });

    const hasPrevSds = computed((): boolean => {
      return currentIndex.value > 0;
    });

    const hasNextSds = computed((): boolean => {
      return currentIndex.value < sdses.value.length - 1;
    });

    const showPrevSds = (): void => {
      const index = currentIndex.value - 1;

      showSdsDetail(sdses.value[index], index);
    };

    const showNextSds = async (): Promise<void> => {
      const index = currentIndex.value + 1;

      showSdsDetail(sdses.value[index], index);
    };

    const suppressPaginationFetch = ref<boolean>(false);

    const resetPagination = (shouldSuppress = false): void => {
      suppressPaginationFetch.value = shouldSuppress;
      page.value = 1;
      nextTick(() => {
        suppressPaginationFetch.value = false;
      });
    };

    const showSdsDetail = async (item: Sds, index: number): Promise<void> => {
      // Navigating to a different SDS (incl. via prev/next) must always drop
      // out of edit mode - otherwise the Save UI stays rendered for a record
      // the user never explicitly chose to edit, regardless of whether they
      // have edit permission on it.
      isEditing.value = false;
      showSdsDetailView.value = true;
      isLoading.value = true;
      try {
        await Promise.all([
          doFetchCompanySds(item.uuid),
          doFetchPdf(item.uuid),
          doFetchSdsEditHistories(item.uuid),
        ]).catch((e) => {
          console.log(e);
        });

        activeSds.value = { ...sds.value };

        canExportCreateSimpleCount.value = 0;
        canExportCreateSimpleCountV3.value = 0;

        currentIndex.value = index;

        productFormItems.value.splice(0);
        productFormItems.value.push(
          ...item.sds_products.map((x, idx) => {
            return {
              text: x.name,
              value: idx,
            };
          })
        );
        materialFormItems.value.splice(0);
        casFormItems.value.splice(0);
        for (const product of sds.value.sds_products) {
          // material_list内のcas_listをcasNumberPatternでマッチングし、1つ以上マッチしたらcanExportCreateSimpleCountV3をインクリメント
          const casList = product.sds_section3.material_list
            .map((x) => x.cas_list)
            .flat()
            .filter((x) => x.cas_number.match(casNumberPattern));
          if (casList.length > 0) {
            canExportCreateSimpleCountV3.value++;
          }

          for (const material of product.sds_section3.material_list) {
            materialFormItems.value.push({
              text: material.material_name,
              value: material.material_name,
            });
            for (const cas of material.cas_list) {
              casFormItems.value.push({
                text: cas.cas_number,
                value: cas.cas_number,
              });
              if (cas.cas_number.match(casNumberPattern)) {
                canExportCreateSimpleCount.value++;
              }
            }
          }
        }
      } finally {
        isLoading.value = false;
      }
    };

    const doFetchPdf = async (sdsId: string): Promise<void> => {
      fetchSdsPdf({
        companyId: readAssertedCompanyId(store),
        sdsId: sdsId,
      });
    };

    const doFetchCompanySds = async (sdsId: string): Promise<void> => {
      await fetchCompanySds({
        sdsId: sdsId,
      });
    };

    const doFetchSdsEditHistories = async (sdsId: string): Promise<void> => {
      if (isFeatureActive(COMPANY_FEATURES.MANAGE_SDS_EDIT_HISTORY)) {
        await fetchSdsEditHistories({
          companyId: readAssertedCompanyId(store),
          sdsId: sdsId,
        });
      }
    };

    const doDeleteCompanySdses = async (): Promise<void> => {
      isLoading.value = true;
      try {
        await deleteCompanySdses({
          companyId: readAssertedCompanyId(store),
          data: {
            ...convertFilterParamsToRequest(currentFilterParams.value),
            sds_uuid_list: isSelectedWholeSdses.value
              ? []
              : selectedSdses.value.map((sds) => sds.uuid),
            is_selected_whole_sdses: isSelectedWholeSdses.value,
            keyword_list: keywordList.value,
          },
        });

        resetStatuses();
        await fetchSdses();
      } catch (error) {
        console.log(error);
      } finally {
        isLoading.value = false;
      }
    };

    const doUpdateCompanySds = async (
      sdsId: string,
      data: Sds,
      tags: TagResponse[],
      departments: Department[]
    ): Promise<void> => {
      isLoading.value = true;
      try {
        await updateCompanySds(
          readAssertedCompanyId(store),
          sdsId,
          data,
          departments,
          tags,
          store.state.main.userProfile
        );
        await doFetchCompanySds(data.uuid);
        await fetchSdses();

        // Update activeSds with fresh data after backend save
        if (showSdsDetailView.value) {
          activeSds.value = { ...sds.value };
        }
      } finally {
        isLoading.value = false;
      }
    };

    const fetchDynamicLists = async () => {
      dynamicLists.value.splice(0);

      const res = await dispatchGetDynamicLists(store, {
        page: 0, // SDSテーブルのページに依存せず常に先頭から全件取得する
        limit: MAX_LIMIT,
        showAll: false,
      });
      if (res && res.data) {
        dynamicLists.value = [...res.data.items];
      }
    };

    const cancelEdit = async (sdsId: string): Promise<void> => {
      isLoading.value = true;
      isChange.value++;
      try {
        await doFetchCompanySds(sdsId);

        productFormItems.value.splice(0);
        productFormItems.value.push(
          ...sds.value.sds_products.map((x, idx) => {
            return {
              text: x.name,
              value: idx,
            };
          })
        );
        materialFormItems.value.splice(0);
        casFormItems.value.splice(0);
        for (const product of sds.value.sds_products) {
          for (const material of product.sds_section3.material_list) {
            materialFormItems.value.push({
              text: material.material_name,
              value: material.material_name,
            });
            for (const cas of material.cas_list) {
              casFormItems.value.push({
                text: cas.cas_number,
                value: cas.cas_number,
              });
            }
          }
        }

        // activeSds を直接編集しているため、キャンセル時には元の値に戻す
        activeSds.value = { ...sds.value };
      } finally {
        isChange.value++;
        isLoading.value = false;
      }
    };

    const confirmSave = (event: {
      preventDefault: () => void;
      returnValue: VueI18n.TranslateResult;
    }): void => {
      if (isEditing.value) {
        event.preventDefault();
        event.returnValue = $t(sharedMessages.confirmLeave);
      }
    };

    const exportCreateSimple = async (sdsId: string): Promise<void> => {
      try {
        dialogShow.value = true;

        const res = await dispatchGetExportCreateSimple(store, {
          sdsId: sdsId,
        });
        if (res) {
          const blob = new Blob([res.data], {
            type: 'application/zip',
          });
          const exportSds = sdses.value.find((x) => x.uuid === sdsId);
          //for type guard
          if (!exportSds) {
            return;
          }
          const link = document.createElement('a');

          const fileName = removeLastPdfExtension(exportSds.name);
          const now = new Date();

          // 日本のタイムゾーンに変換
          const japanTime = new Intl.DateTimeFormat(locale.value, {
            timeZone: 'Asia/Tokyo',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }).format(now);

          // YYYY-MM-DD HH:mm:ss 形式に整形
          const timestampString = japanTime
            .replace(/\//g, '-')
            .replace(/\s/, ' ')
            .replace(/:/g, '');

          link.href = URL.createObjectURL(blob);
          link.download = `${fileName}_${timestampString}_CREATE SIMPLE v2.5.1.zip`;
          link.click();
          URL.revokeObjectURL(link.href);
        }
      } catch (error) {
        console.log(error);
      } finally {
        dialogShow.value = false;
      }
    };

    const exportCreateSimpleV3 = async (sdsId: string): Promise<void> => {
      try {
        dialogShow.value = true;

        const res = await dispatchGetExportCreateSimpleV3(store, {
          sdsId: sdsId,
        });
        if (res) {
          const blob = new Blob([res.data], {
            type: 'application/zip',
          });
          const exportSds = sdses.value.find((x) => x.uuid === sdsId);
          //for type guard
          if (!exportSds) {
            return;
          }

          const link = document.createElement('a');

          const fileName = removeLastPdfExtension(exportSds.name);
          const now = new Date();

          // 日本のタイムゾーンに変換
          const japanTime = new Intl.DateTimeFormat(locale.value, {
            timeZone: 'Asia/Tokyo',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }).format(now);

          // YYYY-MM-DD HH:mm:ss 形式に整形
          const timestampString = japanTime
            .replace(/\//g, '-')
            .replace(/\s/, ' ')
            .replace(/:/g, '');

          link.href = URL.createObjectURL(blob);
          link.download = `${fileName}_${timestampString}_CREATE SIMPLE v3.1.2.zip`;
          link.click();
          URL.revokeObjectURL(link.href);
        }
      } catch (error) {
        console.log(error);
      } finally {
        dialogShow.value = false;
      }
    };

    const exportCreateSimpleV3_2 = async (sdsId: string): Promise<void> => {
      try {
        dialogShow.value = true;

        const res = await dispatchGetExportCreateSimpleV3_2(store, {
          sdsId: sdsId,
        });
        if (res) {
          const blob = new Blob([res.data], {
            type: 'application/zip',
          });
          const exportSds = sdses.value.find((x) => x.uuid === sdsId);
          if (!exportSds) {
            return;
          }

          const link = document.createElement('a');

          const fileName = removeLastPdfExtension(exportSds.name);
          const now = new Date();

          const japanTime = new Intl.DateTimeFormat(locale.value, {
            timeZone: 'Asia/Tokyo',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }).format(now);

          const timestampString = japanTime
            .replace(/\//g, '-')
            .replace(/\s/, ' ')
            .replace(/:/g, '');

          link.href = URL.createObjectURL(blob);
          link.download = `${fileName}_${timestampString}_CREATE SIMPLE v3.2.1.zip`;
          link.click();
          URL.revokeObjectURL(link.href);
        }
      } catch (error) {
        console.log(error);
      } finally {
        dialogShow.value = false;
      }
    };

    const editDynamicListResult = async (
      sdsId: string,
      dynamicListId: number,
      dynamicListResult: SdsDynamicListResult
    ): Promise<void> => {
      try {
        const res = await dispatchPutDynamicListResult(store, {
          sdsId: sdsId,
          dynamicListId: dynamicListId,
          dynamicListResult: dynamicListResult,
        });

        if (res) {
          await doFetchCompanySds(sdsId);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const addDynamicListResult = async (
      sdsId: string,
      dynamicListId: number
    ): Promise<void> => {
      try {
        const res = await dispatchPostDynamicListResult(store, {
          sdsId: sdsId,
          dynamicListId: dynamicListId,
        });
        if (res) {
          await doFetchCompanySds(sdsId);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const deleteDynamicListResult = async (
      sdsId: string,
      dynamicListId: number
    ): Promise<void> => {
      try {
        const res = await dispatchDeleteDynamicListResult(store, {
          sdsId: sdsId,
          dynamicListId: dynamicListId,
        });
        if (res) {
          await doFetchCompanySds(sdsId);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const toggleSdsFilter = (): void => {
      isSdsFilterOpen.value = !isSdsFilterOpen.value;
    };

    const updateSelectedSdses = async (
      form: Omit<UpdateCompanySdsesRequestBody, 'sds_uuid_list'>
    ) => {
      isLoading.value = true;
      try {
        const res = await dispatchUpdateCompanySdses(store, {
          companyId: readAssertedCompanyId(store),
          data: {
            ...form,
            ...convertFilterParamsToRequest(currentFilterParams.value),
            sds_uuid_list: isSelectedWholeSdses.value
              ? []
              : selectedSdses.value.map((sds) => sds.uuid),
            is_selected_whole_sdses: isSelectedWholeSdses.value,
            keyword_list: keywordList.value,
          },
        });
        if (res && res.data) {
          commitAddNotification(store, {
            messageKey: `${$t(m.updateSuccess)}`,
            color: 'success',
          });
        }
        await fetchSdses();
        selectedSdses.value = [];
      } catch (error) {
        console.log(error);
      } finally {
        isLoading.value = false;
      }
    };

    const doDownloadSelectedSdsesCSV = async (): Promise<void> => {
      isDownloadable.value = true;
      try {
        const WHOLE_SELECTION_LIMIT = total.value > 10000 ? 10000 : total.value;
        const MAX_RECORDS = isSelectedWholeSdses.value
          ? WHOLE_SELECTION_LIMIT
          : limit.value;
        const LIMIT = isSelectedWholeSdses.value ? 500 : limit.value;

        // Get current locale
        const currentLocale = locale.value === 'en-US' ? 'en' : 'ja';

        const baseParams = {
          companyId: store.state.main.company.uuid,
          data: {
            ...convertFilterParamsToRequest(currentFilterParams.value),
            is_selected_whole_sdses: isSelectedWholeSdses.value,
            sds_uuid_list: isSelectedWholeSdses.value
              ? []
              : selectedSdses.value.map((sds) => sds.uuid),
            keyword_list: keywordList.value,
            limit: LIMIT,
            locale: currentLocale, // Add locale parameter
          },
        };

        const totalRecords = Math.min(
          MAX_RECORDS,
          isSelectedWholeSdses.value ? MAX_RECORDS : selectedSdses.value.length
        );
        const totalPages = Math.ceil(totalRecords / LIMIT);

        let combinedCsv = '';

        for (let loopPage = 0; loopPage < totalPages; loopPage++) {
          const response = await downloadSelectedSdsesCSV({
            ...baseParams,
            data: {
              ...baseParams.data,
              page: loopPage,
            },
          });

          if (loopPage === 0) {
            combinedCsv = response
              .split('\n')
              .filter((line: string) => line.trim() !== '')
              .join('\n');
          } else {
            // From the second page onwards, remove the header rows and combine them.
            const lines = response.split('\n');
            const content = lines
              .slice(1)
              .filter((line: string) => line.trim() !== '')
              .join('\n');
            if (content) {
              combinedCsv += '\n' + content;
            }
          }
        }

        if (combinedCsv) {
          const encoder = new TextEncoder();
          const data = encoder.encode('\uFEFF' + combinedCsv); // Shift-JIS encoding

          const blob = new Blob([data], {
            type: 'text/csv;charset=shift-jis;',
          });

          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = 'sds-data.csv';
          link.click();
          URL.revokeObjectURL(link.href);
        }
      } catch (error) {
        console.log(error);
        commitAddNotification(store, {
          messageKey: m.csvDownloadFail,
          color: 'error',
        });
      } finally {
        isDownloadable.value = false;
      }
    };

    const chunkArray = (array: csvItem[], chunkSize: number) => {
      const result = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        result.push(array.slice(i, i + chunkSize));
      }
      return result;
    };

    const doUploadSelectedSdsesCSV = async (
      isApproved: boolean,
      hideParentModal: boolean
    ): Promise<void> => {
      isDisableSdsBulkCsvDialog.value = hideParentModal;
      let response = null;
      if (changedCsvData.value.length) {
        try {
          isVisibleCsvBulkUpdateProcessingDialog.value = true;
          const dateTypeChangedCsvData = updateCSVFileDateFormatType(
            changedCsvData.value
          );
          if (dateTypeChangedCsvData.length > 500) {
            const chunkedSdsList = chunkArray(dateTypeChangedCsvData, 500);
            for (let chunk of chunkedSdsList) {
              response = await uploadSelectedSdsesCSV({
                companyId: store.state.main.company.uuid,
                data: {
                  ...convertFilterParamsToRequest(currentFilterParams.value),
                  sds_uuid_list: chunk.map((sds) => sds.sdsID),
                  is_selected_whole_sdses: false,
                  keyword_list: keywordList.value,
                  sds_data: JSON.stringify(chunk),
                  limit: chunk.length,
                  page: page.value - 1,
                  isApproved,
                },
              });
            }
          } else {
            response = await uploadSelectedSdsesCSV({
              companyId: store.state.main.company.uuid,
              data: {
                ...convertFilterParamsToRequest(currentFilterParams.value),
                sds_uuid_list: dateTypeChangedCsvData.map((sds) => sds.sdsID),
                is_selected_whole_sdses: false,
                keyword_list: keywordList.value,
                sds_data: JSON.stringify(dateTypeChangedCsvData),
                limit: isSelectedWholeSdses.value
                  ? limit.value
                  : selectedSdses.value.length,
                page: page.value - 1,
                isApproved,
              },
            });
          }
          if (response) {
            commitAddNotification(store, {
              messageKey: '選択したSDSを更新しました。',
              color: 'success',
            });
            selectedSdses.value = [];
          }
        } catch (error) {
          isLoading.value = false;
          commitAddNotification(store, {
            messageKey: 'SDS の更新に失敗しました。',
            color: 'error ',
          });
        } finally {
          isLoading.value = false;
        }
      } else {
        commitAddNotification(store, {
          messageKey: `${$t(m.sdsDateNonChanged)}`,
          color: 'warning ',
        });
      }
    };

    const csvFileValidate = async (
      csvDataArray: csvItem[],
      hideParentModal: boolean
    ) => {
      changedCsvData.value = [];
      const validationMsgs: ValidationResult = {
        errorCount: 0,
        sdsID: [],
        pdfName: [],
        riskAssessDate: [],
        supplierConfirmationDate: [],
        tag: [],
        department: [],
        productCode: [],
        assignedUser: [],
        notes: [],
      };
      validationErrorMsgs.value = validationMsgs;

      if (csvDataArray.length > 10000) {
        isUploadable.value = false;
        commitAddNotification(store, {
          messageKey: `${$t(m.csvDateLimit)}`,
          color: 'error',
        });
        return false;
      }

      isUploadable.value = true;
      totalChangedNumber.value = csvDataArray.length;
      uploadProcessNumber.value = 0; // Initial value
      nameChangedNumber.value = 0;
      riskAssessDateChangedNumber.value = 0;
      supplierConfirmationDateChangedNumber.value = 0;
      departmentChangedNumber.value = 0;
      tagChangedNumber.value = 0;
      productCodeChangedNumber.value = 0;
      assignedUserChangedNumber.value = 0;
      notesChangedNumber.value = 0;
      fetchSdsesProcessString.value = $t(m.sdsFetchProcess);

      if (isSelectedWholeSdses.value) {
        const requestFilterParams = convertFilterParamsToRequest(
          currentFilterParams.value
        );
        try {
          await fetchCompanySdses({
            companyId: readAssertedCompanyId(store),
            data: {
              ...requestFilterParams,
              sortState: sortState.value,
              keyword_list: keywordList.value,
              limit: 10000,
            },
          });
        } catch (error) {
          isUploadable.value = false;
          commitAddNotification(store, {
            messageKey: 'Fetch SDS failed',
            color: 'error',
          });
          return false;
        }
      }
      fetchSdsesProcessString.value = '';

      // Process CSV data in chunks using requestAnimationFrame
      const chunkSize = limit.value;
      let rowIndex = 1;

      function processChunk(startIndex: number): Promise<boolean> {
        return new Promise((resolve) => {
          const endIndex = Math.min(
            startIndex + chunkSize,
            csvDataArray.length
          );

          for (let i = startIndex; i < endIndex; i++) {
            const csvData = csvDataArray[i];
            let isAdded = false;

            if (!('sdsID' in csvData)) {
              isUploadable.value = false;
              commitAddNotification(store, {
                messageKey: `${$t(m.encodeTypeInvalid)}`,
                color: 'error',
              });
              resolve(false);
              return;
            }

            if (!csvData?.sdsID) {
              const validationMsg: ErrorMsg = {
                rowIndex: rowIndex + 1,
                message: `${$t(m.sdsIdIsNotNull)}`,
              };
              validationMsgs.errorCount += 1;
              validationMsgs.sdsID.push(validationMsg);
            }

            const storedSds = isSelectedWholeSdses.value
              ? wholeSds.value.filter((sds: Sds) => sds.uuid === csvData?.sdsID)
              : sdses.value.filter((sds: Sds) => sds.uuid === csvData?.sdsID);

            if (!storedSds.length && csvData?.sdsID) {
              const validationMsg: ErrorMsg = {
                rowIndex: rowIndex + 1,
                message: `${$t(m.sdsIdIsNotExist)}`,
              };
              validationMsgs.errorCount += 1;
              validationMsgs.sdsID.push(validationMsg);
            }

            const pdfName = csvData?.pdfName ?? '';
            const riskAssessDate =
              csvData?.riskAssessDate === '' ? null : csvData?.riskAssessDate;
            const supplierConfirmationDate =
              csvData?.supplierConfirmationDate === ''
                ? null
                : csvData?.supplierConfirmationDate;
            const csvTags =
              csvData?.tag === ''
                ? []
                : csvData?.tag?.normalize('NFC').trim().split(',');
            const csvDepartments =
              csvData?.department === ''
                ? []
                : csvData?.department?.normalize('NFC').trim().split(',');
            const csvProdutCodes =
              csvData?.productCode === ''
                ? []
                : csvData?.productCode?.normalize('NFC').trim().split(',');
            const csvAssignedUsers =
              csvData?.assignedUser === ''
                ? []
                : csvData?.assignedUser?.normalize('NFC').trim().split(',');

            const assigneeUserIds = users.value
              .map((user) => {
                const isArray = csvAssignedUsers?.some(
                  (assignedUser) => user.email === assignedUser
                );
                if (isArray) return user.uuid;
              })
              .filter(Boolean)
              .join(',');

            csvData.assignedUserIds = assigneeUserIds;

            // Validation sdsName
            if (!pdfName || pdfName.trim() === '') {
              const validationMsg: ErrorMsg = {
                rowIndex: rowIndex + 1,
                message: `${$t(m.sdsNameIsNotNull)}`,
              };
              validationMsgs.errorCount += 1;
              validationMsgs.pdfName.push(validationMsg);
            }

            if (pdfName && pdfName.split('.').pop()?.toLowerCase() !== 'pdf') {
              const validationMsg: ErrorMsg = {
                rowIndex: rowIndex + 1,
                message: `${$t(m.sdsFileIsNotPdf)}`,
              };
              validationMsgs.errorCount += 1;
              validationMsgs.pdfName.push(validationMsg);
            }

            if (
              storedSds[0] &&
              fixStr(pdfName) !== fixStr(storedSds[0]['name'])
            ) {
              nameChangedNumber.value += 1;
              !isAdded && changedCsvData.value.push(csvData);
              isAdded = true;
            }

            // riskAssessDate validation
            if (riskAssessDate && !isValidDateFormat(riskAssessDate)) {
              const validationMsg: ErrorMsg = {
                rowIndex: rowIndex + 1,
                message: `${$t(m.sdsRiskAssessDateType)}`,
              };
              validationMsgs.errorCount += 1;
              validationMsgs.riskAssessDate.push(validationMsg);
            }

            if (
              storedSds[0] &&
              updateChangeDateType(riskAssessDate) !==
                updateChangeDateType(storedSds[0]['assessed_at'])
            ) {
              riskAssessDateChangedNumber.value += 1;
              !isAdded && changedCsvData.value.push(csvData);
              isAdded = true;
            }

            // supplierConfirmationDate validation
            if (
              supplierConfirmationDate &&
              !isValidDateFormat(supplierConfirmationDate)
            ) {
              const validationMsg: ErrorMsg = {
                rowIndex: rowIndex + 1,
                message: `${$t(m.sdsSupplierConfirmationDateType)}`,
              };
              validationMsgs.errorCount += 1;
              validationMsgs.supplierConfirmationDate.push(validationMsg);
            }

            if (
              storedSds[0] &&
              updateChangeDateType(supplierConfirmationDate) !==
                updateChangeDateType(storedSds[0]['supplier_confirmed_at'])
            ) {
              supplierConfirmationDateChangedNumber.value += 1;
              !isAdded && changedCsvData.value.push(csvData);
              isAdded = true;
            }

            // department validation
            if (csvDepartments?.length) {
              const { seen, duplicates } =
                filterDuplicateTextInArray(csvDepartments);
              if (duplicates.length) {
                for (const department of duplicates) {
                  const validationMsg: ErrorMsg = {
                    rowIndex: rowIndex + 1,
                    message: `[${department}] ${$t(m.sdsDepartmentDuplicated)}`,
                  };
                  validationMsgs.errorCount += 1;
                  validationMsgs.department.push(validationMsg);
                }
              }
              for (const department of seen) {
                if (department) {
                  if (!department.match(departmentNamePattern)) {
                    const validationMsg: ErrorMsg = {
                      rowIndex: rowIndex + 1,
                      message: `[${department}] ${$t(
                        m.sdsDepartmentCharatorIssue
                      )}`,
                    };
                    validationMsgs.errorCount += 1;
                    validationMsgs.department.push(validationMsg);
                  }

                  const isDepartmetns = companyDepartments.value.filter(
                    (companyDepartment) =>
                      companyDepartment.name.normalize('NFC').trim() ===
                      department.normalize('NFC').trim()
                  );
                  if (!isDepartmetns.length) {
                    const validationMsg: ErrorMsg = {
                      rowIndex: rowIndex + 1,
                      message: `[${department}] ${$t(m.sdsDepartmentNonExist)}`,
                    };
                    validationMsgs.errorCount += 1;
                    validationMsgs.department.push(validationMsg);
                  }
                }
              }
            }

            if (
              hasPermission(P.SDS_EDIT_OWN_DEPARTMENT) &&
              !hasPermission(P.SDS_EDIT_OTHER_DEPARTMENT)
            ) {
              const isValidDepartmentMsg = validationMsgs.department.filter(
                (msg) => msg.rowIndex === rowIndex + 1
              );
              if (!isValidDepartmentMsg.length) {
                // Match by uuid (via scoped_department_ids, which already
                // includes child departments), not by the user's own exact
                // department names - a CSV row assigning a sub-department
                // of the user's department must still be editable.
                const csvDepartmentUuids = companyDepartments.value
                  .filter((companyDepartment) =>
                    csvDepartments.some(
                      (department) =>
                        companyDepartment.name.normalize('NFC').trim() ===
                        department.normalize('NFC').trim()
                    )
                  )
                  .map((companyDepartment) => ({
                    uuid: companyDepartment.uuid,
                  }));

                if (!hasSdsEditPermission(csvDepartmentUuids)) {
                  const validationMsg: ErrorMsg = {
                    rowIndex: rowIndex + 1,
                    message: `${$t(m.sdsDepartmentNonEdit)}`,
                  };
                  validationMsgs.errorCount += 1;
                  validationMsgs.department.push(validationMsg);
                }
              }
            }

            const currentDepartmentsNameArray = storedSds[0]
              ? storedSds[0].departments.map((currDepartment: Department) =>
                  currDepartment.name.normalize('NFC').trim()
                )
              : [];
            if (
              storedSds[0] &&
              !areSetsEqual(
                new Set(csvDepartments),
                new Set(currentDepartmentsNameArray)
              )
            ) {
              departmentChangedNumber.value += 1;
              !isAdded && changedCsvData.value.push(csvData);
              isAdded = true;
            }

            // tag validation
            if (csvTags?.length) {
              const { seen, duplicates } = filterDuplicateTextInArray(csvTags);
              if (duplicates.length) {
                for (const tag of duplicates) {
                  const validationMsg: ErrorMsg = {
                    rowIndex: rowIndex + 1,
                    message: `[${tag}] ${$t(m.sdsTagDuplicated)}`,
                  };
                  validationMsgs.errorCount += 1;
                  validationMsgs.tag.push(validationMsg);
                }
              }
              for (const tag of seen) {
                if (tag) {
                  if (tag.length > 45) {
                    const validationMsg: ErrorMsg = {
                      rowIndex: rowIndex + 1,
                      message: `[${tag}] ${$t(
                        sharedMessages.sdsTagNameIsNotOver45
                      )}`,
                    };
                    validationMsgs.errorCount += 1;
                    validationMsgs.tag.push(validationMsg);
                  }
                  const isValidTagMsg = validationMsgs.tag.filter(
                    (msg) => msg.rowIndex === rowIndex + 1
                  );
                  if (!isValidTagMsg.length) {
                    const isTags = tags.value.filter(
                      (companyTag) =>
                        companyTag.name.normalize('NFC').trim() ===
                        tag.normalize('NFC').trim()
                    );
                    if (!isTags.length) {
                      const validationMsg: ErrorMsg = {
                        rowIndex: rowIndex + 1,
                        message: `[${tag}] ${$t(m.sdsTagNameInvalid)}`,
                      };
                      validationMsgs.errorCount += 1;
                      validationMsgs.tag.push(validationMsg);
                    }
                  }
                }
              }
            }

            const currentTagsNameArray = storedSds[0]
              ? storedSds[0].tags.map((currTag: Tag) =>
                  currTag.name.normalize('NFC').trim()
                )
              : [];
            if (
              storedSds[0] &&
              !areSetsEqual(new Set(csvTags), new Set(currentTagsNameArray))
            ) {
              tagChangedNumber.value += 1;
              !isAdded && changedCsvData.value.push(csvData);
              isAdded = true;
            }

            // product code validation
            if (csvProdutCodes?.length) {
              const { duplicates } = filterDuplicateTextInArray(csvProdutCodes);
              if (duplicates.length) {
                for (const productCode of duplicates) {
                  const validationMsg: ErrorMsg = {
                    rowIndex: rowIndex + 1,
                    message: `[${productCode}] ${$t(
                      m.sdsProductCodeDuplicated
                    )}`,
                  };
                  validationMsgs.errorCount += 1;
                  validationMsgs.productCode.push(validationMsg);
                }
              }
            }

            const currentProductCodesNameArray = storedSds[0]
              ? storedSds[0].product_code_list.map((currProdCode: string) =>
                  currProdCode.normalize('NFC').trim()
                )
              : [];
            if (
              storedSds[0] &&
              !areSetsEqual(
                new Set(csvProdutCodes),
                new Set(currentProductCodesNameArray)
              )
            ) {
              productCodeChangedNumber.value += 1;
              !isAdded && changedCsvData.value.push(csvData);
              isAdded = true;
            }

            // assignee user validation
            if (csvAssignedUsers?.length) {
              const { seen, duplicates } =
                filterDuplicateTextInArray(csvAssignedUsers);
              if (duplicates.length) {
                for (const assignedUser of duplicates) {
                  const validationMsg: ErrorMsg = {
                    rowIndex: rowIndex + 1,
                    message: `[${assignedUser}] ${$t(
                      m.sdsAssignedUserDuplicated
                    )}`,
                  };
                  validationMsgs.errorCount += 1;
                  validationMsgs.assignedUser.push(validationMsg);
                }
              }
              const activatedUsers = users.value.filter(
                (user) => user.is_active
              );
              for (const assignedUser of seen) {
                const assignedUsers = activatedUsers.some(
                  (user) => user.email === assignedUser
                );
                if (!assignedUsers) {
                  const validationMsg: ErrorMsg = {
                    rowIndex: rowIndex + 1,
                    message: `[${assignedUser}] ${$t(
                      m.sdsAssignedUserUnregistered
                    )}`,
                  };
                  validationMsgs.errorCount += 1;
                  validationMsgs.assignedUser.push(validationMsg);
                }
              }
            }

            const currentAssignedUserNamesArray = storedSds?.[0]?.assignee_users
              ?.length
              ? storedSds[0].assignee_users.map(
                  (assigneeUser: AssignedUser) => {
                    const email = assigneeUser.email ?? '';
                    return email.normalize('NFC').trim();
                  }
                )
              : [];
            if (
              storedSds[0] &&
              !areSetsEqual(
                new Set(csvAssignedUsers),
                new Set(currentAssignedUserNamesArray)
              )
            ) {
              assignedUserChangedNumber.value += 1;
              !isAdded && changedCsvData.value.push(csvData);
              isAdded = true;
            }

            // notes validation and change detection. A CSV without a 備考 column
            // leaves the key undefined, which means "leave the stored note alone".
            const csvNotes = csvData?.notes;
            if (
              csvNotes !== undefined &&
              [...csvNotes].length > SDS_NOTE_MAX_LENGTH
            ) {
              validationMsgs.errorCount += 1;
              validationMsgs.notes.push({
                rowIndex: rowIndex + 1,
                message: $t(m.sdsNoteTooLong, { max: SDS_NOTE_MAX_LENGTH }),
              });
            }
            const currentNotes = storedSds[0] ? storedSds[0].note || '' : '';

            if (
              storedSds[0] &&
              csvNotes !== undefined &&
              csvNotes.normalize('NFC').trim() !==
                currentNotes.normalize('NFC').trim()
            ) {
              notesChangedNumber.value += 1;
              !isAdded && changedCsvData.value.push(csvData);
              isAdded = true;
            }

            rowIndex++;

            if (rowIndex % chunkSize === 0) {
              uploadProcessNumber.value = rowIndex;
            }
          }

          if (endIndex < csvDataArray.length) {
            uploadProcessNumber.value = rowIndex;
            requestAnimationFrame(() => {
              processChunk(endIndex).then(resolve);
            });
          } else {
            resolve(true);
          }
        });
      }

      // Start processing and await completion
      const result = await processChunk(0);
      if (!result) return false;

      // Final UI updates
      uploadProcessNumber.value = rowIndex - 1;

      // update ui after validation
      isUploadable.value = false;
      isDisableSdsBulkCsvDialog.value = hideParentModal;
      isVisibleCSVValidationDialog.value = true;
      validationErrorMsgs.value = validationMsgs;
      fetchSdsesProcessString.value = $t(m.sdsValidationFinished);

      return true;
    };

    const closeBulkCsvUploadForm = () => {
      isDisableSdsBulkCsvDialog.value = false;
    };

    const doDownloadSelectedRiskReassessmentCSV = async (): Promise<void> => {
      // アップロード上限と単位を揃えるため、製品行で数える。
      // バックエンドの RISK_ASSESSMENT_RECOMPUTE_CSV_MAX_ROWS と同値にする。
      const RISK_REASSESSMENT_CSV_MAX_ROWS = 10_000;

      isDownloadable.value = true;
      try {
        const currentLocale = locale.value === 'en-US' ? 'en' : 'ja';

        const baseParams = {
          companyId: store.state.main.company.uuid,
          data: {
            ...convertFilterParamsToRequest(currentFilterParams.value),
            is_selected_whole_sdses: isSelectedWholeSdses.value,
            sds_uuid_list: isSelectedWholeSdses.value
              ? []
              : selectedSdses.value.map((sds) => sds.uuid),
            keyword_list: keywordList.value,
            locale: currentLocale,
          },
        };

        let combinedCsv = '';

        if (isSelectedWholeSdses.value) {
          // 全選択: フィルタ一致 SDS 総件数（total）からページ数を算出し、
          // 全ページを巡回する。製品なしSDSのページ（データ行0）があっても
          // 打ち切らずに後続ページの製品を取りこぼさないようにする。
          const SDS_PER_PAGE = 500;
          const totalSdsPages = Math.ceil(total.value / SDS_PER_PAGE);
          let totalDataRows = 0;

          for (let loopPage = 0; loopPage < totalSdsPages; loopPage++) {
            const response = await downloadSelectedRiskReassessmentCSV({
              ...baseParams,
              data: {
                ...baseParams.data,
                limit: SDS_PER_PAGE,
                page: loopPage,
              },
            });

            const lines = response
              .split('\n')
              .filter((line: string) => line.trim() !== '');

            if (loopPage === 0) {
              if (lines.length > 0) {
                combinedCsv = lines.join('\n');
                totalDataRows += Math.max(0, lines.length - 1); // ヘッダー1行を除く
              }
            } else {
              const dataLines = lines.slice(1); // ヘッダー行を除く
              if (dataLines.length > 0) {
                combinedCsv += '\n' + dataLines.join('\n');
                totalDataRows += dataLines.length;
              }
              // 製品なしSDSページ（dataLines が空）でも break しない
            }

            // 製品行ベースの上限で早期終了
            if (totalDataRows >= RISK_REASSESSMENT_CSV_MAX_ROWS) {
              break;
            }
          }

          // 上限を超えた分を切り詰めてちょうど RISK_REASSESSMENT_CSV_MAX_ROWS 行にする
          if (totalDataRows > RISK_REASSESSMENT_CSV_MAX_ROWS) {
            const allLines = combinedCsv.split('\n');
            const header = allLines[0];
            const dataRows = allLines.slice(1);
            combinedCsv = [
              header,
              ...dataRows.slice(0, RISK_REASSESSMENT_CSV_MAX_ROWS),
            ].join('\n');
          }
        } else {
          // 個別選択: 選択した SDS をまとめて1リクエストで取得する
          const response = await downloadSelectedRiskReassessmentCSV({
            ...baseParams,
            data: {
              ...baseParams.data,
              limit: limit.value,
              page: 0,
            },
          });
          combinedCsv = response
            .split('\n')
            .filter((line: string) => line.trim() !== '')
            .join('\n');
        }

        if (combinedCsv) {
          const blob = new Blob(['\uFEFF' + combinedCsv], {
            type: 'text/csv;charset=utf-8;',
          });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = 'risk-assessment-working-conditions.csv';
          link.click();
          URL.revokeObjectURL(link.href);
        }
      } catch (error) {
        console.log(error);
        commitAddNotification(store, {
          messageKey: m.csvDownloadFail,
          color: 'error',
        });
      } finally {
        isDownloadable.value = false;
      }
    };

    // 一括リスク再評価CSVをアップロードする。
    // ヘッダー・各行の検証はサーバ側で行うため、生ファイルを multipart 送信し、
    // エラー時は detail メッセージを専用ダイアログに表示する。
    const riskReassessmentCsvFileUpload = async (
      file: File,
      hideParentModal: boolean
    ): Promise<void> => {
      isDisableSdsRiskReassessmentDialog.value = hideParentModal;
      isUploadable.value = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        await uploadSelectedRiskReassessmentCSV({
          companyId: store.state.main.company.uuid,
          data: formData,
        });
        commitAddNotification(store, {
          messageKey: `${$t(m.riskReassessmentUploadSuccess)}`,
          color: 'success',
        });
        // モーダルを閉じる（true→false の変化で SdsBottomSheet 側のダイアログが閉じる）
        isDisableSdsRiskReassessmentDialog.value = false;
        selectedSdses.value = [];
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>;
        // CSVバリデーションエラー(400)のみダイアログで内容を表示。
        // それ以外は汎用ハンドラがトーストで通知済み。
        if (axiosError?.response?.status === 400) {
          const data = axiosError.response?.data;
          // errors 配列（行単位の集約エラー）があればそちらを優先して列挙する
          riskReassessmentErrorMessages.value =
            Array.isArray(data?.errors) && data.errors.length > 0
              ? (data.errors as string[])
              : data?.detail
              ? [data.detail]
              : [`${$t(m.riskReassessmentUploadFail)}`];
          isVisibleRiskReassessmentErrorDialog.value = true;
        }
      } finally {
        isUploadable.value = false;
      }
    };

    const doDownloadSelectedSdsesPDF = async (): Promise<void> => {
      try {
        const res = await downloadSelectedSdsesPDFBulk({
          companyId: store.state.main.company.uuid,
          data: {
            ...convertFilterParamsToRequest(currentFilterParams.value),
            sds_uuid_list: isSelectedWholeSdses.value
              ? []
              : selectedSdses.value.map((sds) => sds.uuid),
            is_selected_whole_sdses: isSelectedWholeSdses.value,
            keyword_list: keywordList.value,
          },
        });
        if (res.message === 'success') {
          isVisiblePdfBulkDownloadProcessingDialog.value = true;
        }
      } catch (error) {
        console.log(error);
      } finally {
        isLoading.value = false;
        fetchDownloadPdfCount({ companyId: readAssertedCompanyId(store) });
      }
    };

    const resetStatuses = (shouldSuppress = false) => {
      resetSelectedSdses();
      resetPagination(shouldSuppress);
    };

    const sdsSearchFormRef = ref();
    const handleChangeKeywords = async () => {
      resetStatuses(true);
      await fetchSdses();
      nextTick(() => {
        sdsSearchFormRef.value?.comboboxRef?.focus();
      });
    };

    let cancelFetchSdesesController: AbortController | null = null;
    const fetchSdses = async () => {
      const requestFilterParams = convertFilterParamsToRequest(
        currentFilterParams.value
      );
      if (cancelFetchSdesesController) cancelFetchSdesesController?.abort();
      cancelFetchSdesesController = new AbortController();

      try {
        isLoading.value = true;
        // Fetch Sds count in status bar
        updateSdsCount();

        await Promise.all([
          //TODO: useSdsのanyが取れたら外す
          (
            fetchCompanySdsesCount as ActualUseSdsComposable['fetchCompanySdsesCount']
          )({
            companyId: readAssertedCompanyId(store),
            data: {
              ...requestFilterParams,
              keyword_list: keywordList.value,
            },
            signal: cancelFetchSdesesController.signal,
          }),

          (fetchCompanySdses as ActualUseSdsComposable['fetchCompanySdses'])({
            companyId: readAssertedCompanyId(store),
            data: {
              ...requestFilterParams,
              limit: limit.value,
              page: page.value - 1,
              sortState: sortState.value,
              keyword_list: keywordList.value,
            },
            signal: cancelFetchSdesesController.signal,
          }),
          (
            fetchCompanySdsesCountWithCondition as ActualUseSdsComposable['fetchCompanySdsesCountWithCondition']
          )({
            companyId: readAssertedCompanyId(store),
            data: {
              ...requestFilterParams,
              keyword_list: keywordList.value,
            },
            signal: cancelFetchSdesesController.signal,
          }),
          fetchDownloadPdfCount({
            companyId: readAssertedCompanyId(store),
            signal: cancelFetchSdesesController.signal,
          }),
        ]).catch((e) => {
          if (e.name === 'CanceledError') {
            console.log('Requests aborted');
          }
        });
      } finally {
        if (!cancelFetchSdesesController.signal.aborted) {
          isLoading.value = false;
        }
      }
    };

    const applyFilterParams = async (resetMyFilter = true) => {
      if (resetMyFilter) {
        activeMyFilter.value = undefined;
      }
      applyFormFilterParams();
      resetStatuses(true);
      isSdsFilterOpen.value = false;
      await fetchSdses();
    };

    const resetFilters = async (): void => {
      updateFormFilterParam('tagIds', []);
      updateFormFilterParam('includeNoTag', false);
      updateFormFilterParam('statusGroupId', []);
      updateFormFilterParam('assigneeUsers', []);
      updateFormFilterParam('departmentUuids', []);
      updateFormFilterParam('dynamicListIds', []);
      updateFormFilterParam('sdsName', '');
      updateFormFilterParam('productName', '');
      updateFormFilterParam('manufacturerName', '');
      updateFormFilterParam('productCode', '');
      updateFormFilterParam('revisedAtStartDate', '');
      updateFormFilterParam('revisedAtEndDate', '');
      updateFormFilterParam('createdAtStartDate', '');
      updateFormFilterParam('createdAtEndDate', '');
      updateFormFilterParam('isLatestOnly', false);
      updateFormFilterParam('workingEnvRecommendations', []);
      updateFormFilterParam('healthHazardLevels', []);
      updateFormFilterParam('physicalHazardLevels', []);
      await applyFilterParams();
    };

    const filterSdsesAndShowSdsDetail = async (uuid: string): Promise<void> => {
      keywordList.value.push(uuid);
      await resetFilters();

      currentIndex.value = sdses.value.findIndex((x) => x.uuid === uuid);
      // Open SDS detail modal when the associated SDS is found in sdses
      const item = sdses.value.find((x) => x.uuid === uuid);
      if (item) {
        showSdsDetail(item, sdses.value.indexOf(item));
      }
    };

    onMounted(async (): Promise<void> => {
      //export queryがあればダウンロード処理を行う
      await downloadExportSdses();

      window.onbeforeunload = confirmSave;

      isLoading.value = true;

      try {
        getCompanyDepartments();
        getCompanyDepartmentTree();
        fetchDynamicLists();
        getCompanyUsers({ includeSwitchable: true });
        getCompanyTag();
        const userLocale = readUserLocale(store);
        const language = userLocale === 'en-US' ? 'en' : undefined;
        fetchCompanyExportOperations(readAssertedCompanyId(store), language);
        if (isFeatureActive(COMPANY_FEATURES.MANAGE_SEARCH_CRITERIA)) {
          fetchUserSearchCriteriaList();
        }

        // 部署一覧の「SDS数」からの遷移。ツリー側の件数は配下を含む集計なので、
        // クエリには対象部署 + 全子孫の uuid がカンマ区切りで入ってくる。
        const departmentUuidsQuery = router.currentRoute.query.department_uuids;
        const departmentUuids = (
          Array.isArray(departmentUuidsQuery)
            ? departmentUuidsQuery.join(',')
            : departmentUuidsQuery ?? ''
        )
          .split(',')
          .filter(Boolean);

        const uuid = router.currentRoute.query.uuid;
        if (uuid && typeof uuid === 'string') {
          await filterSdsesAndShowSdsDetail(uuid);
        } else if (departmentUuids.length) {
          updateFormFilterParam('departmentUuids', departmentUuids);
          await applyFilterParams();
        } else {
          await fetchSdses();
        }
      } finally {
        isLoading.value = false;
      }
    });

    const fetchUserSearchCriteriaList = async () => {
      const response = await dispatchGetUserSearchCriteria(store, {
        companyId: readAssertedCompanyId(store),
        userId: readAssertedUserProfile(store).uuid,
      });
      if (response?.data) {
        userSearchCriteriaList.value = response.data;
      }
    };

    const handleApplyMyFilter = (criteria: UserSearchCriteria) => {
      activeMyFilter.value = criteria;
      applyFromMyFilter(criteria);
      applyFilterParams(false);
    };

    const handleSetMyFilter = async (criteria?: UserSearchCriteria) => {
      // undefined is passed by delete action
      activeMyFilter.value = criteria;
      await fetchUserSearchCriteriaList();
    };

    return {
      resetFilters,
      sortState,
      toggleSort,
      keywordList,
      pdfBlob,
      pdfName,
      editHistories,
      limit,
      total,
      page,
      statusList,
      pdfDisplayOptions,
      sds,
      currentFilterParams,
      formFilterParams,
      hasFilterParams,
      statusOptions,
      userOptions,
      tagOptions,
      departmentOptions,
      dynamicListOptions,
      updateFormFilterParam,
      removeFilterParams,
      handleExportSubmit,
      applyFilterParams,
      handleChangeKeywords,
      sdses,
      wholeSds,
      selectedSdses,
      isSelectedAllSdses,
      isSelectedIndeterminateSdses,
      isSelectedWholeSdses,
      pdfDownloadCount,
      pdfMonthlyLimit,
      toggleSelectedSds,
      toggleAllSelectedSdses,
      toggleWholeSelectedSdses,
      resetSelectedSdses,
      isLoading,
      isEditing,
      isUploadable,
      isDownloadable,
      showSdsDetailView,
      isVisibleCSVValidationDialog,
      isVisibleCsvBulkUpdateProcessingDialog,
      isVisiblePdfBulkDownloadProcessingDialog,
      totalChangedNumber,
      uploadProcessNumber,
      fetchSdsesProcessString,
      nameChangedNumber,
      riskAssessDateChangedNumber,
      supplierConfirmationDateChangedNumber,
      departmentChangedNumber,
      tagChangedNumber,
      productCodeChangedNumber,
      assignedUserChangedNumber,
      notesChangedNumber,
      validationErrorMsgs,
      isDisableSdsBulkCsvDialog,
      isDisableSdsRiskReassessmentDialog,
      isVisibleRiskReassessmentErrorDialog,
      riskReassessmentErrorMessages,
      productFormItems,
      materialFormItems,
      casFormItems,
      showPrevSds,
      showNextSds,
      hasPrevSds,
      hasNextSds,
      showSdsDetail,
      doDeleteCompanySdses,
      doUpdateCompanySds,
      cancelEdit,
      resetPagination,
      fetchSdses,
      doFetchCompanySds,
      doFetchPdf,
      doFetchSdsEditHistories,
      exportCreateSimple,
      exportCreateSimpleV3,
      exportCreateSimpleV3_2,
      dialogShow,
      cancelExportCreateSimple,
      canExportCreateSimpleCount,
      canExportCreateSimpleCountV3,
      dynamicLists,
      assigneeUsers,
      tags,
      companyDepartments,
      departmentTree,
      visibleCompanyDepartmentsByOwnOrRole,
      fetchDynamicLists,
      editDynamicListResult,
      addDynamicListResult,
      deleteDynamicListResult,
      activeSds,
      isSdsFilterOpen,
      toggleSdsFilter,
      isChange,
      updateSelectedSdses,
      doDownloadSelectedSdsesPDF,
      closeBulkCsvUploadForm,
      doDownloadSelectedSdsesCSV,
      doUploadSelectedSdsesCSV,
      csvFileValidate,
      doDownloadSelectedRiskReassessmentCSV,
      riskReassessmentCsvFileUpload,
      hasPermission,
      P,
      handleClickProductName,
      handleClickManufacturerName,
      handleClickProductCode,
      handleClickTag,
      handleClickDepartment,
      handleClickStatus,
      handleClickAssigneeUsers,
      exportOperations,
      sharedMessages,
      m,
      users,
      isFeatureActive,
      COMPANY_FEATURES,
      generateInactiveMessage,
      userSearchCriteriaList,
      fetchUserSearchCriteriaList,
      handleApplyMyFilter,
      activeMyFilter,
      handleSetMyFilter,
      suppressPaginationFetch,
      sdsSearchFormRef,
    };
  },
});
